import React, { useState, useEffect, useRef } from 'react';
import './Portfolio.css';

function Portfolio() {
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 50);
      
      // Fade in animation
      document.querySelectorAll('.fade-in').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          el.classList.add('visible');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="new-portfolio">
      {/* Navigation */}
      <nav className={navScrolled ? 'p-nav scrolled' : 'p-nav'}>
        <div className="container">
          <div className="nav-inner">
            <a href="#hero" className="nav-logo">Rajesh Kumar Deekonda</a>
            <ul className="nav-links">
              <li><a href="#about">About</a></li>
              <li><a href="#journey">Journey</a></li>
              <li><a href="#experience">Experience</a></li>
              <li><a href="#work">Work</a></li>
              <li><a href="#expertise">Expertise</a></li>
            </ul>
            <a href="#contact" className="nav-cta">Get in Touch</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" id="hero">
        <div className="hero-bg"></div>
        <div className="hero-grid"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <span className="hero-eyebrow">AI Developer · Fremont, California</span>
              <h1 className="hero-title">
                Building AI at the intersection of <em>healthcare</em> and technology
              </h1>
              <p className="hero-desc">
                I develop production AI systems informed by clinical expertise. My background in pharmacy gives me the domain knowledge that most AI engineers lack the ability to build systems that work in healthcare's regulated reality.
              </p>
              <div className="hero-buttons">
                <a href="#work" className="btn btn-primary">
                  View Projects
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17"/>
                  </svg>
                </a>
                <a href="#contact" className="btn btn-secondary">Contact Me</a>
              </div>
            </div>
            <div className="hero-right">
              <div className="stat-card stat-card-1">
                <div className="stat-label">RAG Accuracy</div>
                <div className="stat-value">90%+</div>
                <div className="stat-sub">Production systems</div>
              </div>
              <div className="stat-card stat-card-2">
                <div className="stat-label">Cost Reduction</div>
                <div className="stat-value">70%</div>
                <div className="stat-sub">vs. enterprise solutions</div>
              </div>
              <div className="stat-card stat-card-3">
                <div className="stat-label">Background</div>
                <div className="stat-value">PharmD</div>
                <div className="stat-sub">+ 2 Master's degrees</div>
              </div>
              <div className="hero-icon-circle">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about" id="about">
        <div className="container">
          <div className="about-wrapper">
            <div className="about-header">
              <div className="section-label">About</div>
              <h2 className="section-title">The convergence of clinical expertise and AI engineering</h2>
            </div>
            <div className="about-text fade-in">
              <p>
                <span className="drop-cap">Most AI builders start with code. I started with patients</span>
              </p>
              <p> 
                Before I ever trained a model, I stood in hospital wards watching brilliant clinicians fight broken systems. Data entered twice because no one automated it. Errors that compliance teams caught only after the damage was done. I didn't just observe it. I felt the weight of it.
              </p>
              <p>
                So I fixed it. Python scripts, clinical data pipelines, 15+ projects at Apollo Hospitals, and a 95% drop in data entry errors. Not because it was my job description, because it was the right problem in front of me.
              </p>
              <p> 
                That instinct doesn't turn off. At Xpresscarz, I built predictive models that quietly added 15% more revenue per rental and caught mechanical failures before they became expensive surprises. Different industry, same mindset: find what breaks, before it breaks.
              </p>
              <p>
                Today at Stellent.AI, I build custom AI systems from the ground up. Small language models trained for specific industries, autonomous agents that run real enterprise workflows, end-to-end automation pipelines, an AI voice and brain cloning platform that delivers what the top enterprise solutions do at 70% lower cost with 99.5% uptime, and RAG systems that people actually depend on in production. Not demos. Not prototypes. Deployed systems with real consequences.
              </p>
              <p>
                On my own, I'm building two things I genuinely believe in. A personal AI assistant called Link AI that acts as your job search partner and project manager in one. And an elderly care application designed to give aging individuals real support and independence through AI.
              </p>
              <p>
                I'm not a researcher who discovered the real world late. I came from it. And that's exactly why I build differently.
              </p>
              
              <div className="metrics-row">
                <div className="metric-box">
                  <div className="metric-num">40%</div>
                  <div className="metric-text">Manual work reduction</div>
                </div>
                <div className="metric-box">
                  <div className="metric-num">15%</div>
                  <div className="metric-text">Revenue increase</div>
                </div>
                <div className="metric-box">
                  <div className="metric-num">95%</div>
                  <div className="metric-text">Error reduction</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="journey" id="journey">
        <div className="container">
          <div className="journey-header fade-in">
            <div className="section-label">Education</div>
            <h2 className="section-title">A deliberate path from clinical practice to AI</h2>
            <p>Three degrees, one vision: building AI systems that understand healthcare.</p>
          </div>
          
          <div className="timeline-grid">
            <div className="timeline-card fade-in">
              <div className="timeline-year">2021</div>
              <h3 className="timeline-title">Doctor of Pharmacy</h3>
              <p className="timeline-org">Kakatiya University, India</p>
              <p className="timeline-desc">Clinical pharmacy, patient safety, drug interaction analysis, healthcare compliance</p>
            </div>
            <div className="timeline-card fade-in">
              <div className="timeline-year">2023</div>
              <h3 className="timeline-title">MS Health Informatics</h3>
              <p className="timeline-org">University of North Texas</p>
              <p className="timeline-desc">Healthcare data systems, EHR integration, clinical analytics, medical informatics</p>
            </div>
            <div className="timeline-card fade-in">
              <div className="timeline-year">2025 – Present</div>
              <h3 className="timeline-title">AI Agent Developer</h3>
              <p className="timeline-org">Stellent.AI, Frisco</p>
              <p className="timeline-desc">RAG pipelines, LangChain, production AI systems, workflow automation</p>
            </div>
            <div className="timeline-card fade-in">
              <div className="timeline-year">2026</div>
              <h3 className="timeline-title">Executive MS in AI</h3>
              <p className="timeline-org">University of the Cumberlands</p>
              <p className="timeline-desc">Advanced ML, deep learning architectures, AI system design</p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="experience" id="experience">
        <div className="container">
          <div className="experience-header fade-in">
            <div className="section-label">Experience</div>
            <h2 className="section-title">Professional background</h2>
          </div>
          
          <div className="exp-list">
            <div className="exp-card fade-in">
              <div className="exp-meta">
                <div className="exp-period">Nov 2025 – Present</div>
                <div className="exp-company">Stellent.AI</div>
                <div className="exp-location">Frisco, TX</div>
              </div>
              <div className="exp-content">
                <h4>AI Agent Developer & Integrator</h4>
                <div className="exp-points">
                  <p className="exp-point">Build custom AI agents for document processing, customer inquiries, and data extraction cutting manual work by 40%</p>
                  <p className="exp-point">Set up RAG pipelines using LangChain and pgvector achieving 90%+ accuracy on responses</p>
                  <p className="exp-point">Wire together multiple AI services (OpenAI, custom models, cloud APIs) using n8n most deployments involve 8-10 integrations</p>
                </div>
              </div>
            </div>
            
            <div className="exp-card fade-in">
              <div className="exp-meta">
                <div className="exp-period">Jun 2024 – Oct 2025</div>
                <div className="exp-company">Xpresscarz</div>
                <div className="exp-location">Dallas, TX</div>
              </div>
              <div className="exp-content">
                <h4>Data Analyst & AI Optimization</h4>
                <div className="exp-points">
                  <p className="exp-point">Built predictive models in Python to forecast rental demand helped ops team plan inventory effectively</p>
                  <p className="exp-point">Created dynamic pricing system pulling competitor rates revenue per rental increased 15%</p>
                  <p className="exp-point">Analyzed telematics data to predict maintenance issues reduced surprise repairs by 30%</p>
                </div>
              </div>
            </div>
            
            <div className="exp-card fade-in">
              <div className="exp-meta">
                <div className="exp-period">Apr 2019 – Jul 2021</div>
                <div className="exp-company">Apollo Hospitals</div>
                <div className="exp-location">Telangana, India</div>
              </div>
              <div className="exp-content">
                <h4>Clinical Data/Research Associate</h4>
                <div className="exp-points">
                  <p className="exp-point">Ran 15+ clinical data projects, improving completion rates by 20% through tracking automation</p>
                  <p className="exp-point">Wrote Python scripts for data validation saved 10 hours/week on repetitive work</p>
                  <p className="exp-point">Fixed CRF validation process data entry errors dropped 95%, database locks happened 25% faster</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work/Projects Section */}
      <section className="work" id="work">
        <div className="container">
          <div className="work-header fade-in">
            <div className="section-label">Featured Work</div>
            <h2 className="section-title">Projects that solve real problems</h2>
          </div>
          
          {/* Featured Project - Voxatar */}
          <div className="project-featured fade-in">
            <div className="project-visual">
              <div className="project-visual-pattern"></div>
              <div className="project-visual-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
              </div>
            </div>
            <div className="project-info">
              <div className="project-label">Featured Project</div>
              <h3 className="project-name">AI Voice & Brain Cloning</h3>
              <p className="project-desc">
                Enterprise platform that turns your voice and knowledge into a working AI version of you. No compromises on capability. Just 70% lower cost and 99.5% uptime.
              </p>
              <div className="project-tags">
                <span className="tag">OpenAI</span>
                <span className="tag">ElevenLabs</span>
                <span className="tag">pgvector</span>
                <span className="tag">Next.js</span>
                <span className="tag">AssemblyAI</span>
                <span className="tag">n8n</span>
              </div>
              <a href="#" className="project-link">
                In Development
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Other Projects Grid */}
          <div className="projects-grid">
            <div className="project-card fade-in">
              <div className="project-label">AI Assistant</div>
              <h3 className="project-name">Link AI</h3>
              <p className="project-desc">
                Voice-enabled AI assistant with conversational memory and RAG-powered knowledge retrieval. Siri-like voice input, natural TTS output, persistent context.
              </p>
              <div className="project-tags">
                <span className="tag">React</span>
                <span className="tag">Flask</span>
                <span className="tag">OpenAI</span>
                <span className="tag">Supabase</span>
              </div>
            </div>
            
            <div className="project-card fade-in">
              <div className="project-label">Healthcare AI</div>
              <h3 className="project-name">Clinical Risk Pipeline</h3>
              <p className="project-desc">
                Cardiovascular risk prediction using Framingham Score methodology. Identifies high-risk patients with 85% accuracy for clinical decision support.
              </p>
              <div className="project-tags">
                <span className="tag">Python</span>
                <span className="tag">TensorFlow</span>
                <span className="tag">Pandas</span>
              </div>
            </div>
            
            <div className="project-card fade-in">
              <div className="project-label">Job Search</div>
              <h3 className="project-name">JobHunt Pro</h3>
              <p className="project-desc">
                Full-stack application tracker with AI-powered job matching. Scrapes Greenhouse/Lever APIs, analyzes qualification fit, tracks pipeline.
              </p>
              <div className="project-tags">
                <span className="tag">React</span>
                <span className="tag">Flask</span>
                <span className="tag">Railway</span>
              </div>
            </div>
            
            <div className="project-card fade-in">
              <div className="project-label">Enterprise RAG</div>
              <h3 className="project-name">Knowledge System</h3>
              <p className="project-desc">
                Production RAG pipeline for semantic search over 10K+ documents. Custom embeddings with pgvector storage and intelligent retrieval.
              </p>
              <div className="project-tags">
                <span className="tag">LangChain</span>
                <span className="tag">pgvector</span>
                <span className="tag">PostgreSQL</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="expertise" id="expertise">
        <div className="container">
          <div className="expertise-header fade-in">
            <div className="section-label">Expertise</div>
            <h2 className="section-title">Technical capabilities</h2>
          </div>
          
          <div className="expertise-grid">
            <div className="expertise-card fade-in">
              <div className="expertise-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>
                  <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>
                </svg>
              </div>
              <h3 className="expertise-title">AI & Machine Learning</h3>
              <div className="expertise-tags">
                <span className="expertise-tag">RAG Systems</span>
                <span className="expertise-tag">LLM Integration</span>
                <span className="expertise-tag">OpenAI API</span>
                <span className="expertise-tag">LangChain</span>
                <span className="expertise-tag">NLP</span>
              </div>
            </div>
            
            <div className="expertise-card fade-in">
              <div className="expertise-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <polyline points="16 18 22 12 16 6"/>
                  <polyline points="8 6 2 12 8 18"/>
                </svg>
              </div>
              <h3 className="expertise-title">Development</h3>
              <div className="expertise-tags">
                <span className="expertise-tag">Python</span>
                <span className="expertise-tag">JavaScript</span>
                <span className="expertise-tag">React/Next.js</span>
                <span className="expertise-tag">Flask/FastAPI</span>
                <span className="expertise-tag">PostgreSQL</span>
              </div>
            </div>
            
            <div className="expertise-card fade-in">
              <div className="expertise-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
                </svg>
              </div>
              <h3 className="expertise-title">Cloud & Infrastructure</h3>
              <div className="expertise-tags">
                <span className="expertise-tag">AWS</span>
                <span className="expertise-tag">Docker</span>
                <span className="expertise-tag">Supabase</span>
                <span className="expertise-tag">n8n</span>
                <span className="expertise-tag">Railway</span>
              </div>
            </div>
            
            <div className="expertise-card fade-in">
              <div className="expertise-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
                </svg>
              </div>
              <h3 className="expertise-title">Healthcare Technology</h3>
              <div className="expertise-tags">
                <span className="expertise-tag">Health Informatics</span>
                <span className="expertise-tag">Clinical Data</span>
                <span className="expertise-tag">HIPAA</span>
                <span className="expertise-tag">Medidata Rave</span>
              </div>
            </div>
            
            <div className="expertise-card fade-in">
              <div className="expertise-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                </svg>
              </div>
              <h3 className="expertise-title">Voice AI</h3>
              <div className="expertise-tags">
                <span className="expertise-tag">ElevenLabs</span>
                <span className="expertise-tag">AssemblyAI</span>
                <span className="expertise-tag">Web Speech API</span>
                <span className="expertise-tag">TTS/STT</span>
              </div>
            </div>
            
            <div className="expertise-card fade-in">
              <div className="expertise-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <ellipse cx="12" cy="5" rx="9" ry="3"/>
                  <path d="M3 5V19A9 3 0 0 0 21 19V5"/>
                  <path d="M3 12A9 3 0 0 0 21 12"/>
                </svg>
              </div>
              <h3 className="expertise-title">Vector Databases</h3>
              <div className="expertise-tags">
                <span className="expertise-tag">pgvector</span>
                <span className="expertise-tag">Pinecone</span>
                <span className="expertise-tag">Embeddings</span>
                <span className="expertise-tag">Semantic Search</span>
              </div>
            </div>
          </div>
          
          <div className="certs fade-in">
            <h3 className="certs-title">Certifications</h3>
            <div className="certs-list">
              <div className="cert">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Good Clinical Practice (GCP) — Novartis/Coursera, valid through 2034
              </div>
              <div className="cert">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Python for Data Science and Machine Learning — Coursera
              </div>
              <div className="cert">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                AWS Cloud Practitioner — In Progress
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact" id="contact">
        <div className="container">
          <div className="contact-inner fade-in">
            <div className="section-label">Contact</div>
            <h2 className="contact-title">Let's build something together</h2>
            <p className="contact-desc">
              I'm currently open to AI/ML Engineer and Forward Deployed Engineer roles, particularly in healthcare AI.
            </p>
            <a href="mailto:deekondarajeshkumar469@gmail.com" className="contact-email">
              deekondarajeshkumar469@gmail.com
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 17L17 7M17 7H7M17 7V17"/>
              </svg>
            </a>
            <div className="contact-links">
              <a href="https://www.linkedin.com/in/rajesh-kumar-deekonda-213743303/" target="_blank" rel="noopener noreferrer" className="contact-link">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect width="4" height="12" x="2" y="9"/>
                  <circle cx="4" cy="4" r="2"/>
                </svg>
                LinkedIn
              </a>
              <a href="https://github.com/rdeekonda37-creator" target="_blank" rel="noopener noreferrer" className="contact-link">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/>
                  <path d="M9 18c-4.51 2-5-2-7-2"/>
                </svg>
                GitHub
              </a>
              <a href="tel:+14697021103" className="contact-link">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                469-702-1103
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="p-footer">
        <div className="container">
          <p>© 2026 Rajesh Kumar Deekonda. Built with React.</p>
        </div>
      </footer>
    </div>
  );
}

export default Portfolio;
